import axios from "axios";
import i18n from "../i18n";
import { API_BASE_URL } from "../config";

const token = () => localStorage.getItem("lk_access");
const refreshToken = () => localStorage.getItem("lk_refresh");

/** Extract a user-facing message from an axios error, falling back to a default. */
export function apiError(e: unknown, fallback: string): string {
  const d = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof d === "string" && d ? d : fallback;
}

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

// Separate instance for /auth/refresh: the response interceptor on `api`
// retries 401s by calling doRefresh(). Routing the refresh call through `api`
// would make it await its own promise — a circular wait that hangs the UI on
// a spinner instead of logging the user out.
const refreshApi = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((cfg) => {
  const t = token();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

let isRefreshing = false;
let refreshPromise: Promise<string> | null = null;

/** Call /auth/refresh and store the new tokens. Returns the new access token. */
export async function doRefresh(): Promise<string> {
  if (isRefreshing && refreshPromise) return refreshPromise;
  const rt = refreshToken();
  if (!rt) throw new Error("No refresh token");
  isRefreshing = true;
  refreshPromise = refreshApi
    .post("/auth/refresh", { refresh_token: rt })
    .then((res) => {
      localStorage.setItem("lk_access", res.data.access_token);
      localStorage.setItem("lk_refresh", res.data.refresh_token);
      return res.data.access_token as string;
    })
    .finally(() => {
      isRefreshing = false;
      refreshPromise = null;
    });
  return refreshPromise;
}

/** Clear tokens and redirect to login. */
export function forceLogout() {
  // Revoke the refresh token server-side before dropping it — best effort,
  // the session is dead locally either way. Goes through refreshApi because
  // `api`'s 401 interceptor could recurse back into forceLogout.
  const rt = refreshToken();
  if (rt) refreshApi.post("/auth/logout", { refresh_token: rt }).catch(() => {});
  localStorage.removeItem("lk_access");
  localStorage.removeItem("lk_refresh");
  if (!window.location.pathname.startsWith("/admin/login")) {
    window.location.replace("/admin/login");
  }
}

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const original = err.config;
    if (err.response?.status === 401 && !original._retried) {
      // Try to refresh the token and retry the original request
      try {
        const newToken = await doRefresh();
        original._retried = true;
        original.headers.Authorization = `Bearer ${newToken}`;
        return api(original);
      } catch {
        forceLogout();
      }
    } else if (err.response?.status === 401) {
      forceLogout();
    } else if (err.response?.status === 403) {
      // Backend permission denials carry generic English details
      // ("Insufficient permissions" / "Insufficient role"). Replace them with
      // a localized message so catch blocks show something meaningful.
      const d = err.response.data?.detail;
      if (typeof d !== "string" || d.startsWith("Insufficient") || d === "Forbidden") {
        err.response.data = { ...err.response.data, detail: i18n.t("common.forbidden") };
      }
    }
    return Promise.reject(err);
  }
);
